import React from 'react';
import MapContainer from '../../js/Components/Map';
import TituloPagina from '../../js/Components/PageTitle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck,
  faClock,
  faEnvelope,
  faLocationDot,
  faMapLocationDot,
  faMessage,
  faPhone,
  faShieldHeart,
  faTooth
} from '@fortawesome/free-solid-svg-icons';
import { branches, DentalPuenteBranch, getOpenStreetMapUrl, getTelUrl, getWhatsappUrl } from '../data/branches';

const FACEBOOK_URL = 'https://www.facebook.com/DentalPuente-2072424749721597';

const Contact: React.FC = () => {
  const [selectedBranchId, setSelectedBranchId] = React.useState<DentalPuenteBranch['id']>('gigantera');
  const selectedBranch = branches.find((branch) => branch.id === selectedBranchId) || branches[0];

  return (
    <div>
      <TituloPagina title="DentalPuente | Contacto" pageSlug="contact" />

      <main className="contact-page content-wrap">
        <section className="contact-hero" aria-labelledby="contact-title">
          <div className="contact-hero-content">
            <span className="dp-eyebrow">Contacto Dental Puente</span>
            <h1 id="contact-title">Agenda tu cita sin vueltas.</h1>
            <p>
              Ya contamos con dos sucursales. Escríbenos por WhatsApp para elegir ubicación, confirmar disponibilidad y llegar con tu cita agendada.
            </p>

            <div className="contact-hero-actions">
              <a href={getWhatsappUrl(selectedBranch)} target="_blank" rel="noopener noreferrer" className="contact-primary-button">
                <FontAwesomeIcon icon={faMessage} />
                Solicitar cita por WhatsApp
              </a>
              <a href={getTelUrl(selectedBranch)} className="contact-secondary-button">
                <FontAwesomeIcon icon={faPhone} />
                Llamar a {selectedBranch.shortName}
              </a>
            </div>
          </div>

          <aside className="contact-quick-card" aria-label="Información rápida">
            <div className="contact-quick-icon">
              <FontAwesomeIcon icon={faTooth} />
            </div>
            <div>
              <span>{selectedBranch.status}</span>
              <strong>{selectedBranch.phoneDisplay}</strong>
            </div>
          </aside>
        </section>

        <section className="contact-branches-panel" id="sucursales" aria-label="Sucursales DentalPuente">
          <div className="contact-branches-header">
            <div>
              <span className="dp-eyebrow">Nuestras sucursales</span>
              <h2>Elige dónde quieres atenderte</h2>
              <p>Plaza La Gigantera ya abrió. Por ahora esa sucursal atiende únicamente con cita previamente confirmada.</p>
            </div>
          </div>

          <div className="contact-branch-tabs" role="tablist" aria-label="Selecciona una sucursal">
            {branches.map((branch) => (
              <button
                type="button"
                className={branch.id === selectedBranchId ? 'active' : ''}
                onClick={() => setSelectedBranchId(branch.id)}
                role="tab"
                aria-selected={branch.id === selectedBranchId}
                key={branch.id}
              >
                <span>{branch.badge}</span>
                {branch.shortName}
              </button>
            ))}
          </div>

          <div className="contact-branch-showcase">
            <article className="contact-selected-branch-card">
              <span className="contact-branch-status">{selectedBranch.status}</span>
              <h3>{selectedBranch.name}</h3>
              <p>{selectedBranch.note}</p>

              <div className="contact-selected-branch-list">
                <div>
                  <FontAwesomeIcon icon={faLocationDot} />
                  <span>{selectedBranch.address}</span>
                </div>
                <div>
                  <FontAwesomeIcon icon={faClock} />
                  <span>{selectedBranch.schedule}</span>
                </div>
                <div>
                  <FontAwesomeIcon icon={faPhone} />
                  <span>{selectedBranch.phoneDisplay}</span>
                </div>
              </div>

              <div className="contact-branch-actions">
                <a href={getWhatsappUrl(selectedBranch)} target="_blank" rel="noopener noreferrer" className="contact-primary-button">
                  <FontAwesomeIcon icon={faMessage} />
                  WhatsApp
                </a>
                <a href={getOpenStreetMapUrl(selectedBranch)} target="_blank" rel="noopener noreferrer" className="contact-secondary-button">
                  <FontAwesomeIcon icon={faMapLocationDot} />
                  Cómo llegar
                </a>
              </div>
            </article>

            <div className="contact-map-panel contact-map-panel-branch">
              <div className="contact-map-header">
                <div>
                  <span className="contact-card-kicker">Mapa</span>
                  <h2>{selectedBranch.shortName}</h2>
                </div>
                <FontAwesomeIcon icon={faMapLocationDot} />
              </div>
              <div className="contact-map-frame">
                <MapContainer branch={selectedBranch} />
              </div>
            </div>
          </div>
        </section>

        <section className="contact-grid" aria-label="Opciones de contacto">
          <article className="contact-card contact-card-whatsapp">
            <div className="contact-card-icon">
              <FontAwesomeIcon icon={faMessage} />
            </div>
            <div>
              <span className="contact-card-kicker">Respuesta directa</span>
              <h2>WhatsApp</h2>
              <p>La vía más rápida para agendar, mover una cita o resolver preguntas antes de tu consulta.</p>
              <a href={getWhatsappUrl(selectedBranch)} target="_blank" rel="noopener noreferrer" className="contact-card-link">
                Escribir por WhatsApp
              </a>
            </div>
          </article>

          <article className="contact-card">
            <div className="contact-card-icon">
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div>
              <span className="contact-card-kicker">Horario</span>
              <h2>Cita previa</h2>
              <p><strong>{selectedBranch.schedule}</strong><br />Confirma disponibilidad antes de asistir.</p>
            </div>
          </article>

          <article className="contact-card">
            <div className="contact-card-icon">
              <FontAwesomeIcon icon={faLocationDot} />
            </div>
            <div>
              <span className="contact-card-kicker">Ubicación</span>
              <h2>{selectedBranch.shortName}</h2>
              <p>{selectedBranch.address}</p>
              <a href={getOpenStreetMapUrl(selectedBranch)} target="_blank" rel="noopener noreferrer" className="contact-card-link">
                Abrir mapa
              </a>
            </div>
          </article>
        </section>

        <section className="contact-main-panel contact-main-panel-single">
          <div className="contact-info-panel">
            <span className="dp-eyebrow">Antes de visitarnos</span>
            <h2>Te ayudamos a llegar con todo claro.</h2>

            <div className="contact-info-list">
              <div className="contact-info-item">
                <FontAwesomeIcon icon={faCalendarCheck} />
                <div>
                  <h3>Agenda confirmada</h3>
                  <p>Confirma sucursal, horario y disponibilidad por WhatsApp antes de asistir.</p>
                </div>
              </div>

              <div className="contact-info-item">
                <FontAwesomeIcon icon={faShieldHeart} />
                <div>
                  <h3>Consulta tranquila</h3>
                  <p>Cuéntanos tus dudas y el motivo de tu visita para orientarte mejor.</p>
                </div>
              </div>

              <div className="contact-info-item">
                <FontAwesomeIcon icon={faEnvelope} />
                <div>
                  <h3>Redes sociales</h3>
                  <p>También puedes seguirnos y compartir tu experiencia.</p>
                  <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="contact-inline-link">
                    Ver Facebook
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Contact;
